import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import { Button, useNotify, useRecordContext, useRefresh } from "react-admin";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import { DoctorantCandidacyStatus } from "utils/constants";

const ActionButtons = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  const [actions, setActions] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!record?.id) return undefined;

    const fetchActions = async () => {
      try {
        const { data } = await dataProvider.customRequest(doctoralRequests.getCandidacyActions(record.id), null, "GET");
        if (!cancelled) setActions(data);
      } catch (err) {
        console.error("Неуспешно зареждане на действията за кандидатурата", err);
        if (!cancelled) setActions({ canApprove: false, canDecline: false, isTerminal: false });
      }
    };

    fetchActions();

    return () => {
      cancelled = true;
    };
  }, [record?.id, record?.status]);

  if (!record || !actions) return null;

  if (!actions.canApprove && !actions.canDecline) {
    return (
      <Typography color="text.secondary">
        {actions.isTerminal
          ? "Кандидатурата е в окончателен статус — не са възможни действия."
          : "Нямате права за действия по тази кандидатура."}
      </Typography>
    );
  }

  const isObjectionReview = record.status === DoctorantCandidacyStatus.ObjectionUnderReview;

  const handleApprove = async () => {
    await dataProvider.customRequest(doctoralRequests.approveCandidacy(record.id), null, "POST");
    notify(
      isObjectionReview ? "Възражението е прието — кандидатът е допуснат до изпит" : "Кандидатът е допуснат до изпит",
      { type: "success" },
    );
    refresh();
  };

  const handleDecline = async () => {
    await dataProvider.customRequest(doctoralRequests.declineCandidacy(record.id), null, "POST");
    notify(isObjectionReview ? "Възражението е отхвърлено" : "Кандидатът не е допуснат до изпит", { type: "success" });
    refresh();
  };

  const approveLabel = isObjectionReview ? "Приеми възражението" : "Допусни до изпит";
  const declineLabel = isObjectionReview ? "Отхвърли възражението" : "Не допускай";

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      {actions.canApprove && (
        <ConfirmModal
          titleContent={isObjectionReview ? "Приемане на възражение" : "Допускане до изпит"}
          saveTitle={approveLabel}
          onConfirm={handleApprove}
          triggerButton={
            <Button
              label={approveLabel}
              variant="contained"
              color="primary"
              startIcon={<CheckIcon />}
            />
          }
          message={
            isObjectionReview
              ? "Сигурни ли сте, че искате да приемете възражението и да допуснете кандидата до изпит?"
              : "Сигурни ли сте, че искате да допуснете този кандидат до изпит?"
          }
        />
      )}
      {actions.canDecline && (
        <ConfirmModal
          titleContent={isObjectionReview ? "Отхвърляне на възражение" : "Недопускане до изпит"}
          saveTitle={declineLabel}
          onConfirm={handleDecline}
          triggerButton={
            <Button
              label={declineLabel}
              variant="contained"
              color={isObjectionReview ? "error" : "warning"}
              startIcon={isObjectionReview ? <CloseIcon /> : <AssignmentReturnIcon />}
            />
          }
          message={
            isObjectionReview
              ? "Сигурни ли сте, че искате да отхвърлите възражението? Решението е окончателно."
              : "Кандидатът няма да бъде допуснат до изпит и ще получи нотификация с възможност да подаде възражение в 7-дневен срок."
          }
        />
      )}
    </Box>
  );
};

export default ActionButtons;
